import { mutationOptions, queryOptions } from "@tanstack/react-query";
import {
  CHANGE_EMAIL_VERSION_2,
  GET_USER_INF,
  SET_CALL_WAITING,
  UPD_USER_INF,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { sha1 } from "~/lib/crypto";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import { EVENT_NAMES, trackEvent } from "~/lib/tracking";
import { uploadAvatar } from "~/lib/uploadImage";
import { useAuthStore } from "~/stores/useAuthStore";
import type { User } from "~/types/user";
import { toGender } from "~/types/user";
import type {
  ChangeEmailFormValues,
  ChangeEmailResponse,
  GetUserInfoResponse,
  MyProfile,
  UpdateCallWaitingRequest,
  UpdateCallWaitingResponse,
  UpdateUserInfoRequest,
  UpdateUserInfoResponse,
} from "./types";

const toUser = (data: GetUserInfoResponse): User => {
  return {
    id: data.user_id,
    name: data.user_name,
    email: data.email,
    gender: toGender(data.gender),
    age: data.age,
    avatarUrl: data.ava_id ?? null,
    region: data.region ?? null,
  };
};

export const myProfileQueryOptions = queryOptions({
  queryKey: queryKeys.myProfile.detail(),
  queryFn: () => apiClient<GetUserInfoResponse>(GET_USER_INF),
});

export const updateUserInfoMutationOptions = mutationOptions({
  mutationFn: (data: UpdateUserInfoRequest) =>
    apiClient<UpdateUserInfoResponse>(
      UPD_USER_INF,
      data as Record<string, unknown>,
    ),
  onSuccess: () => {
    trackEvent(EVENT_NAMES.EDIT_PROFILE);
  },
});

export const updateCallWaitingMutationOptions = mutationOptions({
  mutationFn: (data: UpdateCallWaitingRequest) =>
    apiClient<UpdateCallWaitingResponse>(
      SET_CALL_WAITING,
      data as Record<string, unknown>,
    ),
  onMutate: async (variables) => {
    await queryClient.cancelQueries({
      queryKey: queryKeys.myProfile.detail(),
    });
    const previous = queryClient.getQueryData<MyProfile>(
      queryKeys.myProfile.detail(),
    );
    if (previous) {
      queryClient.setQueryData<MyProfile>(queryKeys.myProfile.detail(), {
        ...previous,
        video_call_waiting: variables.video_call_waiting,
        voice_call_waiting: variables.voice_call_waiting,
      });
    }
    return { previous };
  },
  onError: (_err, _variables, context) => {
    if (context?.previous) {
      queryClient.setQueryData(queryKeys.myProfile.detail(), context.previous);
    }
  },
});

export const changeEmailMutationOptions = mutationOptions({
  mutationFn: async (data: ChangeEmailFormValues) => {
    const hashedNewPwd = await sha1(data.newPassword);
    return apiClient<ChangeEmailResponse>(CHANGE_EMAIL_VERSION_2, {
      email: data.email,
      original_pwd: data.currentPassword,
      new_pwd: hashedNewPwd,
    });
  },
  onSuccess: (data) => {
    queryClient.setQueryData(queryKeys.myProfile.detail(), data);
    useAuthStore.getState().setUser(toUser(data));
  },
});

export const uploadAvatarMutationOptions = mutationOptions({
  mutationFn: (blob: Blob) => uploadAvatar(blob),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
    trackEvent(EVENT_NAMES.UPLOAD_PROFILE_AVATAR);
  },
});
